import React, { useState, useEffect } from 'react';
import { getAdminStats, getProducts, getOrders, getClients, getContactInquiries, createProduct, updateProduct, deleteProduct, updateOrderStatus } from '@/services/api';
import { Users, Package, ShoppingCart, DollarSign, Mail, Plus, Edit, Trash2, Eye } from 'lucide-react';
import ProductForm from './ProductForm';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'overview') {
        const statsData = await getAdminStats();
        setStats(statsData);
      } else if (activeTab === 'products') {
        const productsData = await getProducts();
        setProducts(productsData);
      } else if (activeTab === 'orders') {
        const ordersData = await getOrders();
        setOrders(ordersData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      } else if (activeTab === 'clients') {
        const clientsData = await getClients();
        setClients(clientsData);
      } else if (activeTab === 'inquiries') {
        const inquiriesData = await getContactInquiries();
        setInquiries(inquiriesData.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProduct(id);
        loadData();
      } catch (error) {
        alert('Failed to delete product');
      }
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      loadData();
    } catch (error) {
      alert('Failed to update order status');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <div className="bg-gray-900 text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <p className="text-gray-300 mt-1">Manage your wine spirits business</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <div className="flex overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview', icon: DollarSign },
              { id: 'products', label: 'Products', icon: Package },
              { id: 'orders', label: 'Orders', icon: ShoppingCart },
              { id: 'clients', label: 'Clients', icon: Users },
              { id: 'inquiries', label: 'Inquiries', icon: Mail }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center space-x-2 px-6 py-4 font-medium border-b-2 transition ${
                  activeTab === tab.id
                    ? 'border-gray-900 text-gray-900'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <tab.icon size={20} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && stats && (
              <div data-testid="overview-tab">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-600 text-sm">Total Clients</p>
                        <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="total-clients">{stats.total_clients}</p>
                      </div>
                      <Users className="text-gray-400" size={40} />
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-600 text-sm">Total Products</p>
                        <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="total-products">{stats.total_products}</p>
                      </div>
                      <Package className="text-gray-400" size={40} />
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-600 text-sm">Total Orders</p>
                        <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="total-orders">{stats.total_orders}</p>
                      </div>
                      <ShoppingCart className="text-gray-400" size={40} />
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-600 text-sm">Pending Orders</p>
                        <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="pending-orders">{stats.pending_orders}</p>
                      </div>
                      <ShoppingCart className="text-yellow-400" size={40} />
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-600 text-sm">Total Revenue</p>
                        <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="total-revenue">${stats.total_revenue.toFixed(2)}</p>
                      </div>
                      <DollarSign className="text-green-400" size={40} />
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-600 text-sm">Brand Inquiries</p>
                        <p className="text-3xl font-bold text-gray-900 mt-1" data-testid="total-inquiries">{stats.total_inquiries}</p>
                      </div>
                      <Mail className="text-blue-400" size={40} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'products' && (
              <div data-testid="products-tab">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Products</h2>
                  <button
                    onClick={() => {
                      setEditingProduct(null);
                      setShowProductForm(true);
                    }}
                    data-testid="add-product-btn"
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
                  >
                    <Plus size={20} />
                    <span>Add Product</span>
                  </button>
                </div>
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Region</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {products.map((product) => (
                        <tr key={product.id} data-testid={`product-row-${product.id}`}>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <img src={product.image_url || 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=100'} alt={product.name} className="w-12 h-12 rounded object-cover mr-3" />
                              <span className="font-medium text-gray-900">{product.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">{product.product_type}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{product.region}, {product.country}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{product.stock}</td>
                          <td className="px-6 py-4 text-sm space-x-2">
                            <button
                              onClick={() => {
                                setEditingProduct(product);
                                setShowProductForm(true);
                              }}
                              data-testid={`edit-product-${product.id}`}
                              className="text-blue-600 hover:text-blue-800 inline-block"
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteProduct(product.id)}
                              data-testid={`delete-product-${product.id}`}
                              className="text-red-600 hover:text-red-800 inline-block"
                            >
                              <Trash2 size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'orders' && (
              <div data-testid="orders-tab">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Orders</h2>
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {orders.map((order) => (
                        <tr key={order.id} data-testid={`admin-order-row-${order.id}`}>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{order.id.substring(0, 8)}...</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{order.client_business_name}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{new Date(order.created_at).toLocaleDateString()}</td>
                          <td className="px-6 py-4 text-sm font-semibold text-gray-900">${order.total.toFixed(2)}</td>
                          <td className="px-6 py-4 text-sm">
                            <select
                              value={order.status}
                              onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                              data-testid={`order-status-select-${order.id}`}
                              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            >
                              <option value="pending">Pending</option>
                              <option value="processing">Processing</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <button
                              onClick={() => setSelectedOrder(order)}
                              data-testid={`view-admin-order-${order.id}`}
                              className="text-gray-900 hover:text-gray-700"
                            >
                              <Eye size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'clients' && (
              <div data-testid="clients-tab">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Clients</h2>
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Business Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registered</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {clients.map((client) => (
                        <tr key={client.id} data-testid={`client-row-${client.id}`}>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{client.business_name || client.email}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{client.business_type || 'N/A'}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{client.email}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{client.phone || 'N/A'}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{new Date(client.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'inquiries' && (
              <div data-testid="inquiries-tab">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Inquiries</h2>
                <div className="space-y-4">
                  {inquiries.map((inquiry) => (
                    <div key={inquiry.id} className="bg-white p-6 rounded-xl shadow-sm" data-testid={`inquiry-${inquiry.id}`}>
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="font-semibold text-lg text-gray-900">{inquiry.name}</h3>
                          <p className="text-sm text-gray-600">{inquiry.email} | {inquiry.phone || 'No phone'}</p>
                          {inquiry.company && <p className="text-sm text-gray-600">Company: {inquiry.company}</p>}
                        </div>
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                          {inquiry.inquiry_type}
                        </span>
                      </div>
                      <p className="text-gray-700 mb-2">{inquiry.message}</p>
                      <p className="text-xs text-gray-500">Received: {new Date(inquiry.created_at).toLocaleString()}</p>
                    </div>
                  ))}</div>
              </div>
            )}
          </>
        )}

        {showProductForm && (
          <ProductForm
            product={editingProduct}
            onClose={() => {
              setShowProductForm(false);
              setEditingProduct(null);
            }}
            onSave={loadData}
          />
        )}

        {selectedOrder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Order Details</h2>
                  <p className="text-gray-600 text-sm mt-1">Order ID: {selectedOrder.id}</p>
                </div>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Client Information</h3>
                  <p className="text-gray-700">{selectedOrder.client_business_name}</p>
                  <p className="text-sm text-gray-600">{selectedOrder.client_email}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Delivery Address</h3>
                  <p className="text-gray-700">{selectedOrder.delivery_address}</p>
                  {selectedOrder.delivery_notes && (
                    <p className="text-gray-600 text-sm mt-2">Notes: {selectedOrder.delivery_notes}</p>
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Items</h3>
                  {selectedOrder.items.map((item, index) => (
                    <div key={index} className="flex justify-between p-3 bg-gray-50 rounded-lg mb-2">
                      <div>
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t pt-4">
                  <div className="text-center text-gray-600">
                    <p className="text-sm">Pricing updated weekly</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
